/**
 * Informes. Rejilla de 6 tarjetas.
 *
 * El catálogo viene de la API (`GET /api/reports`), no está escrito en el cliente. La
 * generación real del PDF/Excel es fase 2, así que la acción «Descargar» se muestra atenuada
 * con la explicación en el `title` en lugar de fingir que descarga algo.
 */
import { useQuery } from '@tanstack/react-query';
import { api, queryKeys } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { Icon } from '../components/Icon';
import { ErrorState } from '../components/EmptyState';
import { SkeletonGrid, SkeletonPageHeader } from '../components/Skeleton';
import styles from './ReportsPage.module.css';

export function ReportsPage() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.reports,
    queryFn: api.reports,
  });

  const header = (
    <PageHeader
      title="Informes financieros"
      subtitle="Cuenta de resultados, cash flow y libro de inquilinos, listos para exportar."
    />
  );

  if (isPending) {
    return (
      <section>
        <SkeletonPageHeader />
        <SkeletonGrid count={6} height={190} />
      </section>
    );
  }

  if (error) {
    return (
      <section>
        {header}
        <ErrorState error={error} onRetry={() => void refetch()} />
      </section>
    );
  }

  return (
    <section>
      {header}
      {/* Lista compacta: un informe por fila, con sus formatos y la descarga a la derecha. */}
      <Card className={styles.list}>
        {data.data.map((report) => (
          <div key={report.id} className={styles.row}>
            <div className={styles.text}>
              <div className={styles.name}>{report.name}</div>
              <div className={styles.description}>{report.description}</div>
            </div>
            <span className={styles.formats}>{report.formats.map((f) => f.toUpperCase()).join(' · ')}</span>
            <div
              className={
                report.available ? styles.action : `${styles.action} ${styles.actionDisabled}`
              }
              title={
                report.available
                  ? undefined
                  : 'La generación de informes llega en la fase 2. Ver la hoja de ruta del README.'
              }
            >
              <Icon name="download" size={15} strokeWidth={2} />
              Descargar
            </div>
          </div>
        ))}
      </Card>
    </section>
  );
}
